import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import moment from 'moment';
import { TableColumn } from '../models/table.models';
import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class UtilService {
  private dialog = inject(MatDialog);
  private snackbar = inject(MatSnackBar);

  openPopupCustom<T, D>(
    component: any,
    data?: D,
    width = '400px',
  ): Observable<T> {
    const dialogRef = this.dialog.open(component, {
      maxWidth: width,
      width,
      data,
    });
    return dialogRef.afterClosed();
  }

  openSnackBar(
    message: string,
    action: string,
    type: 'success' | 'error',
  ): void {
    this.snackbar.open(message, action, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: type === 'success' ? 'success-snackbar' : 'error-snackbar',
      duration: 5000,
    });
  }

  /**
   * Calculate the date range for the current week
   * @returns An object containing fromDate and toDate in YYYY-MM-DD format
   */
  getWeeklyDateRange(): { fromDate: string; toDate: string } {
    const today = moment();
    const yesterday = moment(today).subtract(1, 'days');
    const sevenDaysAgo = moment(yesterday).subtract(6, 'days'); // 7 days total including yesterday

    return {
      fromDate: sevenDaysAgo.format('YYYY-MM-DD'),
      toDate: yesterday.format('YYYY-MM-DD'),
    };
  }

  /**
   * Calculate the date range for the current month
   * @returns An object containing fromDate (1st of month) and toDate (today) in YYYY-MM-DD format
   */
  getMonthlyDateRange(): { fromDate: string; toDate: string } {
    const today = moment();
    const startOfMonth = moment(today).startOf('month');

    return {
      fromDate: startOfMonth.format('YYYY-MM-DD'),
      toDate: today.format('YYYY-MM-DD'),
    };
  }

  /**
   * Parses a date string (YYYY-MM-DD) into a Date object.
   * @param dateString The date string to parse.
   * @returns A Date object or null if parsing fails.
   */
  parseDateString(dateString: string): Date | null {
    const date = moment(dateString, 'YYYY-MM-DD');
    return date.isValid() ? date.toDate() : null;
  }

  /**
   * Formats a Date object into a string (YYYY-MM-DD).
   * @param date The Date object to format.
   * @returns A string in YYYY-MM-DD format or an empty string if the date is null.
   */
  formatDate(date: Date | null): string {
    return date ? moment(date).format('YYYY-MM-DD') : '';
  }

  transformChartDataToTable(chartData: any): {
    columns: TableColumn[];
    data: any[];
  } {
    if (!chartData?.series || !chartData?.xAxis?.categories) {
      return { columns: [], data: [] };
    }

    const categories = chartData.xAxis.categories;
    const series = chartData.series;

    // Define table columns
    const columns: TableColumn[] = [
      { key: 'category', header: 'Category', sortable: false },
    ];
    categories.forEach((category: string) => {
      let columnStyleClass = ''; // Default: no specific class
      if (category !== 'Less than 7 days') {
        if (
          category === 'Between 8 and 15 days' ||
          category === 'Between 16 and 30 days'
        ) {
          columnStyleClass = 'amber-column';
        } else {
          columnStyleClass = 'red-column';
        }
      }
      columns.push({
        key: this.normalizeKey(category),
        header: category,
        sortable: false,
        styleClass: columnStyleClass, // Assign the determined class
      });
    });

    // Transform series data to table data
    const data: any[] = series.map((s: any) => {
      const row: any = { category: s.name };
      s.data.forEach((value: any, index: number) => {
        const category = categories[index];
        row[this.normalizeKey(category)] = value;
      });
      return row;
    });

    return { columns, data };
  }

  normalizeKey(key: string): string {
    return key.replace(/\s+/g, '');
  }
}
